import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Favorite } from "../../models/interfaces";

interface FavoritesState {
    items: Favorite[];
    loaded: boolean;
}

const initialState: FavoritesState = {
    items: [],
    loaded: false,
};

const favoritesSlice = createSlice({
    name: "favorites",
    initialState,
    reducers: {
        addFavorite: (state, action: PayloadAction<Favorite>) => {
            // Avoid duplicates based on docGuid + guid + pageNumber + tstart
            const exists = state.items.some(
                (f) =>
                    f.docGuid === action.payload.docGuid &&
                    f.guid === action.payload.guid &&
                    f.pageNumber === action.payload.pageNumber &&
                    f.tstart === action.payload.tstart
            );
            if (!exists) {
                state.items.push(action.payload);
            }
        },
        removeFavorite: (state, action: PayloadAction<string>) => {
            state.items = state.items.filter((f) => f.id !== action.payload);
        },
        removeFavoriteByDocAndGuid: (state, action: PayloadAction<{ docGuid: string; guid: string }>) => {
            state.items = state.items.filter(
                (f) => !(f.docGuid === action.payload.docGuid && f.guid === action.payload.guid)
            );
        },
        setFavorites: (state, action: PayloadAction<Favorite[]>) => {
            state.items = action.payload;
            state.loaded = true;
        },
        toggleFavorite: (state, action: PayloadAction<Favorite>) => {
            const index = state.items.findIndex(
                (f) => f.docGuid === action.payload.docGuid && f.guid === action.payload.guid
            );
            if (index >= 0) {
                state.items.splice(index, 1);
            } else {
                state.items.push(action.payload);
            }
        },
        renameByDocGuid: (state, action: PayloadAction<{ docGuid: string; fileName: string }>) => {
            state.items.forEach((f) => {
                if (f.docGuid === action.payload.docGuid) {
                    f.fileName = action.payload.fileName;
                }
            });
        },
        relabelById: (state, action: PayloadAction<{ id: string; label: string }>) => {
            const item = state.items.find((f) => f.id === action.payload.id);
            if (item) {
                item.label = action.payload.label;
            }
        },
        clearFavorites: (state) => {
            state.items = [];
        },
    },
});

export const {
    addFavorite,
    removeFavorite,
    removeFavoriteByDocAndGuid,
    setFavorites,
    toggleFavorite,
    renameByDocGuid,
    relabelById,
    clearFavorites,
} = favoritesSlice.actions;

export default favoritesSlice.reducer;
